import type { IHeatmapFilterConfig, IHeatmapFilterConfigMap } from '~/modules/heatmap/types';

import {
  CartIcon,
  ChatReferralIcon,
  ClockIcon,
  MegaphoneIcon,
  MoneyIcon,
  PersonIcon,
} from '@shopify/polaris-icons';
import { EHeatmapFilterType, EVisitorType } from '~/modules/heatmap/types';
import { JourneyIcon, ExperimentsIcon } from '~/presentation/icons';
import { useFeatureFlagChecker } from '~/modules/apps';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

interface IHeatmapFilterConfigResult {
  filterConfigs: IHeatmapFilterConfig[];
  filterConfigMap: IHeatmapFilterConfigMap;
  configInfoBy: (filterType: EHeatmapFilterType) => IHeatmapFilterConfig;
}

export const useHeatmapFilterConfig = (): IHeatmapFilterConfigResult => {
  const { t } = useTranslation();
  const { isEnabledHeatmapV2, isEnabledHeatmapV3 } = useFeatureFlagChecker();

  const filterConfigMap = useMemo<IHeatmapFilterConfigMap>(
    () => ({
      [EHeatmapFilterType.VISITOR_JOURNEY]: {
        id: EHeatmapFilterType.VISITOR_JOURNEY,
        label: t('Visitor journey'),
        icon: JourneyIcon,
        data: {
          title: t('Visitor journey'),
          description: t('Show heatmap data for sessions that match your custom page path.'),
          helperText: {
            title: t('Helps you:'),
            description: t(
              'Understand user behavior across the specific entry, visited, or exit pages you define.',
            ),
          },
        },
        tags: ['funnel', 'path', 'flow', 'journey'],
      },
      [EHeatmapFilterType.VISITOR_TYPE]: {
        id: EHeatmapFilterType.VISITOR_TYPE,
        label: t('Visitor type'),
        icon: PersonIcon,
        defaultValue: {
          value: [EVisitorType.NEW],
        },
        data: {
          title: t('Visitor type'),
          description: t('Show heatmap data from visitors in selected markets.'),
          helperText: {
            title: t('Helps you:'),
            description: t('See regional or language-specific behavior.'),
          },
        },
        tags: ['customer', 'first-time', 'repeat', 'return', 'new'],
        disabled: !isEnabledHeatmapV2,
      },
      [EHeatmapFilterType.MARKET]: {
        id: EHeatmapFilterType.MARKET,
        label: t('Market'),
        icon: PersonIcon,
        defaultValue: {
          value: [],
        },
        data: {
          title: t('Market'),
          description: t('Show heatmap data from visitors in selected markets.'),
          helperText: {
            title: t('Helps you:'),
            description: t('See regional or language-specific behavior.'),
          },
        },
        tags: ['country', 'region', 'location', 'geography', 'language'],
        hide: true,
        disabled: !isEnabledHeatmapV2,
      },
      [EHeatmapFilterType.CAMPAIGN]: {
        id: EHeatmapFilterType.CAMPAIGN,
        label: t('A/B experiment'),
        icon: ExperimentsIcon,
        data: {
          title: t('A/B experiment'),
          description: t('Show heatmap data from experiments that tested this page.'),
          helperText: {
            title: t('Helps you:'),
            description: t('Compare user behavior across different versions.'),
          },
        },
        tags: ['A/B', 'campaign', 'control', 'variant', 'version', 'testing', 'AB'],
      },
      [EHeatmapFilterType.TRAFFIC_SOURCE]: {
        id: EHeatmapFilterType.TRAFFIC_SOURCE,
        label: t('Traffic source'),
        icon: MegaphoneIcon,
        data: {
          title: t('Traffic source'),
          description: t('Show heatmap data based on where visitors came from.'),
          helperText: {
            title: t('Helps you:'),
            description: t(
              'Evaluate how visitors from different sources interact with your content.',
            ),
          },
        },
        tags: ['channel', 'utm', 'acquisition', 'social', 'search'],
        disabled: !isEnabledHeatmapV2,
      },
      [EHeatmapFilterType.PURCHASE_TYPE]: {
        id: EHeatmapFilterType.PURCHASE_TYPE,
        label: t('Purchase / Non-purchase'),
        icon: CartIcon,
        data: {
          title: t('Purchase / Non-purchase'),
          description: t('Show heatmap data based on whether visitors purchase or not.'),
          helperText: {
            title: t('Helps you:'),
            description: t('Compare behaviors between converting and non-converting visitors.'),
          },
        },
        tags: [
          'buyer',
          'non-buyer',
          'converted',
          'not converted',
          'no order',
          'non-order',
          'order',
        ],
        disabled: !isEnabledHeatmapV2,
      },
      [EHeatmapFilterType.TIME_ON_PAGE]: {
        id: EHeatmapFilterType.TIME_ON_PAGE,
        label: t('Time on page'),
        icon: ClockIcon,
        data: {
          title: t('Time on page'),
          description: t('Show heatmap data based on the amount of time spent on the page.'),
          helperText: {
            title: t('Helps you:'),
            description: t('Distinguish between quick visits and deep engagements.'),
          },
        },
        tags: ['duration', 'spent', 'hour', 'minute', 'second', 'period'],
        disabled: !isEnabledHeatmapV2,
      },
      [EHeatmapFilterType.ORDER_VALUE]: {
        id: EHeatmapFilterType.ORDER_VALUE,
        label: t('Order value'),
        icon: MoneyIcon,
        data: {
          title: t('Order value'),
          description: t(
            'Show heatmap data from sessions that resulted in a specific order value range.',
          ),
          helperText: {
            title: t('Helps you:'),
            description: t(
              'Distinguish between behaviors related to higher- or lower-value sessions.',
            ),
          },
        },
        tags: ['revenue', 'money', 'earn'],
        disabled: !isEnabledHeatmapV2,
      },
      [EHeatmapFilterType.REFERRAL_SOURCE]: {
        id: EHeatmapFilterType.REFERRAL_SOURCE,
        label: t('Referral source'),
        icon: ChatReferralIcon,
        data: {
          title: t('Referral source'),
          description: t(
            'Show heatmap data from visitors who come from a specific referring source.',
          ),
          helperText: {
            title: t('Helps you:'),
            description: t(
              "Understand how users behave when they arrive from different sources like Google, Facebook, or a partner's blog.",
            ),
          },
        },
        tags: ['referring', 'external', 'source', 'link', 'site'],
        disabled: !isEnabledHeatmapV3,
      },
    }),
    [t, isEnabledHeatmapV2, isEnabledHeatmapV3],
  );

  const filterConfigs = useMemo<IHeatmapFilterConfig[]>(
    () => Object.values(filterConfigMap).filter((config) => !config.hide),
    [filterConfigMap],
  );

  const configInfoBy = useCallback(
    (filterType: EHeatmapFilterType) => {
      return filterConfigMap[filterType]!;
    },
    [filterConfigMap],
  );

  return {
    filterConfigs,
    filterConfigMap,
    configInfoBy,
  };
};
